import { useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { isChairRole } from '@dental-flow/rbac';
import {
  staffApi,
  type StaffMember,
  QUALIFICATION_CATEGORY_LABELS,
  type QualificationCategory,
} from '@/api/clinic.api';
import { settingsApi } from '@/api/settings.api';
import { branchesApi } from '@/api/branches.api';
import { hrApi } from '@/api/hr.api';
import { ApiError } from '@/api/client';
import { Button, Input } from '@/components/ui';
import { Modal } from '@/components/ui/Modal';
import { useSession } from '@/app/session';
import { printBrandColor } from '@/lib/branding';
import './SettingsPage.css';
import { useTranslation } from 'react-i18next';
import { i18nMap } from '@/lib/i18n-map';
import { sameNameMember } from './staff-duplicates';

/**
 * Formularul unui membru al echipei, scos din `SettingsPage`.
 *
 * Stătea înăuntrul celui mai mare fișier din aplicație, neexportat — deci
 * singurul loc din care se putea adăuga sau edita un om era Setări → Echipă,
 * iar lista de personal din Resurse umane trimitea acolo printr-un subtitlu.
 * Aceeași treabă, două ecrane, și un drum între ele.
 *
 * Mutarea e mecanică: aceleași câmpuri, aceeași validare, același DTO. Ce se
 * schimbă e că formularul are acum un singur loc în care trăiește, deci o
 * regulă adăugată aici — cum e „`showInCalendar` urmează rolul" — ajunge
 * deodată în amândouă ecranele, în loc să fie copiată și uitată într-unul.
 */

export const ROLE_LABELS = i18nMap<string>('common', {
  admin: 'administrator',
  proprietar: 'proprietar',
  medic: 'medic',
  medic_ortodont: 'medicOrtodont',
  asistent: 'asistent',
  receptie: 'receptie',
  contabil: 'contabil',
  hr: 'resurseUmane',
  readonly: 'doarCitire',
});

const SPECS = [
  'Medic Ortodont',
  'Medic Stomatolog',
  'Medic Rezident',
  'Implantolog',
  'Chirurg OMF',
  'Igienist',
  'Profesor',
  'Recepție',
  'Administrator',
];

/** „12 500,50" → 12500.5; gol → null; altceva → NaN, ca formularul să spună. */
function salaryOf(text: string): number | null {
  const t = text.replace(/\s/g, '').replace(',', '.');
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? n : Number.NaN;
}

/** Gol → null (norma clinicii); un întreg 0–365 → el; altceva → NaN. */
function leaveDaysOf(text: string): number | null {
  const t = text.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isInteger(n) && n >= 0 && n <= 365 ? n : Number.NaN;
}

/** Funcțiile obișnuite dintr-o clinică dentară, propuse la scriere; rămâne text liber. */
const JOB_POSITIONS = [
  'Medic stomatolog generalist',
  'Medic stomatolog terapeut',
  'Endodont',
  'Parodontolog',
  'Ortodont',
  'Medic stomatolog ortoped (protetician)',
  'Chirurg stomatolog / oro-maxilo-facial',
  'Implantolog',
  'Stomatolog pediatru',
  'Stomatolog estetician',
  'Radiolog oro-maxilo-facial',
  'Igienist dentar',
  'Asistent stomatolog',
  'Tehnician dentar',
  'Coordonator de tratament',
  'Administrator',
  'Medic șef',
  'Director',
] as const;

export function StaffDialog({
  member,
  onClose,
  onSaved,
}: {
  member: StaffMember | null;
  onClose: () => void;
  /** `saved` și `isNew` lasă apelantul să ofere contul de logare imediat după
      ce s-a creat un membru nou, nu într-un al doilea pas ușor de uitat. */
  onSaved: (saved?: StaffMember, isNew?: boolean) => void;
}) {
  const { t } = useTranslation('settings');
  /*
   * Programul clinicii, pentru un coleg nou.
   *
   * Orele din Setări → Program nu erau citite de nimeni, iar un membru nou
   * pornea de la 09:00–18:00 scrise aici. O clinică deschisă 07:00–15:00 le
   * schimba de fiecare dată, pentru fiecare om. Se citesc din
   * `GET /settings/look`, deci și dintr-un ecran deschis de cineva fără
   * `setari.vede` — dialogul se deschide și din Resurse umane.
   */
  const look = useQuery({
    queryKey: ['settings', 'look'],
    queryFn: settingsApi.look,
    staleTime: 5 * 60_000,
  });

  /*
   * Fișa de personal: funcția, IDNP-ul, adresa, salariul.
   *
   * API-ul le primea dintotdeauna și niciun ecran nu le scria, deci contractul
   * ieșea cu IDNP și adresă de completat de mână, iar statul de plată avea
   * salariul 0 pentru oricine nu venise prin Recrutare. Se arată doar cui le
   * vede — cine n-are `personal.vede` nici nu le primește în listă, iar un câmp
   * gol trimis de el le-ar șterge. Salariul cere în plus `salarii.vede`, la fel
   * ca pe server, care refuză o schimbare de salariu fără ea.
   */
  const { can, hasFeature } = useSession();
  const canSeePersonal = can('personal.vede');
  const canSeeSalary = canSeePersonal && can('salarii.vede');

  /*
   * Zilele de concediu: gol înseamnă norma clinicii (PERS-2).
   *
   * Coloana avea `DEFAULT 28`, deci fiecare om avea „28 ale lui" și norma din
   * Setări → Personal nu ajungea la nimeni. Acum câmpul gol arată norma ca
   * placeholder — cifra care se aplică — iar o valoare scrisă e o excepție
   * pentru omul acela. `GET /hr/rules` e deschis oricui e autentificat.
   */
  const hrRules = useQuery({
    queryKey: ['hr', 'rules'],
    queryFn: hrApi.rules,
    // Resurse umane off: the API answers 404, and the field shows no norm.
    enabled: canSeePersonal && hasFeature('hr'),
    staleTime: 5 * 60_000,
  });

  const [form, setForm] = useState({
    jobTitle: member?.jobTitle ?? '',
    idnp: member?.idnp ?? '',
    address: member?.address ?? '',
    baseSalary: member?.baseSalary != null ? String(member.baseSalary) : '',
    leaveDaysYear: member?.leaveDaysYear != null ? String(member.leaveDaysYear) : '',
    name: member?.name ?? '',
    role: member?.role ?? 'medic',
    level: member?.level ?? 'senior',
    spec: member?.spec ?? '',
    qualificationCategory: (member?.qualificationCategory ?? '') as QualificationCategory | '',
    color: member?.color ?? printBrandColor(),
    email: member?.email ?? '',
    phone: member?.phone ?? '',
    showInCalendar: member?.showInCalendar ?? isChairRole(member?.role ?? 'medic'),
    workStart: member?.workStart ?? '',
    workEnd: member?.workEnd ?? '',
    lunchStart: member?.lunchStart ?? '13:00',
    lunchEnd: member?.lunchEnd ?? '14:00',
    active: member?.active ?? true,
    branchId: member?.branchId ?? '',
  });
  const [error, setError] = useState<string | null>(null);

  /*
   * Un al doilea „Dr. Ion Rusu" trecea fără niciun cuvânt. Adresa de email
   * dublată o refuză serverul (409); numele doar se semnalează aici, fiindcă
   * doi oameni pot purta, rar, același nume. Aceeași cheie ca lista din
   * Pacienți, deci de obicei nicio cerere în plus.
   */
  const roster = useQuery({ queryKey: ['staff'], queryFn: () => staffApi.list() });
  const twin = sameNameMember(form.name, roster.data ?? [], member?.id);

  // Filialele clinicii, ca un membru nou să fie legat de cabinet din prima, nu
  // după. Câmpul apare doar la o clinică cu mai multe filiale.
  const branches = useQuery({ queryKey: ['branches'], queryFn: () => branchesApi.list() });

  /*
   * „Afișează în calendar" urmează rolul, până când cineva o atinge.
   *
   * Coloana are `DEFAULT true`, deci orice om adăugat intra în orar oricare
   * i-ar fi fost rolul — iar o recepție are coloana ei în calendar fără să
   * primească vreodată o programare. La Orthodontica au ajuns așa șase
   * coloane, toate cu zero programări într-un an.
   *
   * Legarea de rol nu are voie să fie mai tare decât omul: dacă bifa a fost
   * atinsă, rolul n-o mai mișcă. Un asistent care face igienizări are coloana
   * lui pentru că cineva a cerut-o, nu pentru că i-a permis-o implicitul.
   */
  const calTouched = useRef(false);

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => {
      if (k === 'showInCalendar') calTouched.current = true;
      const next = { ...f, [k]: v };
      if (k === 'role' && !calTouched.current) next.showInCalendar = isChairRole(String(v));
      return next;
    });

  const save = useMutation({
    mutationFn: () => {
      // `active` is deliberately absent from the create body: a new member is
      // always active, and the API rejects a property it does not accept
      // rather than ignoring it.
      const { active, branchId, jobTitle, idnp, address, baseSalary, leaveDaysYear, ...rest } =
        form;
      const body = {
        ...rest,
        ...(canSeePersonal
          ? {
              jobTitle: jobTitle.trim() || null,
              idnp: idnp.trim() || null,
              address: address.trim() || null,
              leaveDaysYear: leaveDaysOf(leaveDaysYear),
            }
          : {}),
        ...(canSeeSalary ? { baseSalary: salaryOf(baseSalary) } : {}),
        // Ce se vede în câmpuri e ce se salvează: netastate, orele sunt ale
        // clinicii, nu un șir gol pe care API-ul l-ar refuza.
        workStart: form.workStart || look.data?.workStart || '09:00',
        workEnd: form.workEnd || look.data?.workEnd || '18:00',
        spec: form.spec || null,
        qualificationCategory: form.qualificationCategory || null,
        email: form.email || null,
        phone: form.phone || null,
        lunchStart: form.lunchStart || null,
        lunchEnd: form.lunchEnd || null,
        branchId: branchId || null,
      };
      return member ? staffApi.update(member.id, { ...body, active }) : staffApi.create(body);
    },
    onSuccess: (saved) => onSaved(saved, !member),
    onError: (err) => setError(err instanceof ApiError ? err.message : t('nuSAPutut')),
  });

  return (
    <Modal title={member ? member.name : t('membruNou')} onClose={onClose} width={520}>
      <form
        className="sd"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          if (!form.name.trim()) return setError(t('numeleEsteObligatoriu'));
          if (canSeeSalary && Number.isNaN(salaryOf(form.baseSalary) ?? 0)) {
            return setError(t('salariulTrebuieSaFie'));
          }
          if (canSeePersonal && Number.isNaN(leaveDaysOf(form.leaveDaysYear) ?? 0)) {
            return setError(t('zileleDeConcediuSunt'));
          }
          save.mutate();
        }}
      >
        <Input
          label="Nume"
          required
          autoFocus
          value={form.name}
          onChange={(e) => set('name', e.target.value)}
          hint={
            twin
              ? `Există deja în echipă ${twin.name}${twin.email ? ` (${twin.email})` : ''}. Verifică să nu fie aceeași persoană.`
              : undefined
          }
        />

        <div className="sd__row">
          <label className="field">
            <span className="field__label">{t('rol')}</span>
            <div className="field__control">
              <select value={form.role} onChange={(e) => set('role', e.target.value)}>
                {Object.entries(ROLE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
          </label>

          <label className="field">
            <span className="field__label">Nivel</span>
            <div className="field__control">
              <select value={form.level} onChange={(e) => set('level', e.target.value)}>
                <option value="senior">Senior</option>
                <option value="junior">Junior</option>
              </select>
            </div>
          </label>

          {/* Atestarea ministerului, nu nivelul din aplicație: „Senior" e o regulă
              de drepturi, iar formularul 039-1/e cere categoria de calificare în
              antet. Doar la cine lucrează la scaun — ceilalți n-au formular. */}
          {isChairRole(form.role) ? (
            <label className="field">
              <span className="field__label">{t('categoriaDeCalificare')}</span>
              <div className="field__control">
                <select
                  value={form.qualificationCategory}
                  onChange={(e) =>
                    set('qualificationCategory', e.target.value as QualificationCategory | '')
                  }
                >
                  <option value="">{t('faraCategorie')}</option>
                  {(Object.keys(QUALIFICATION_CATEGORY_LABELS) as QualificationCategory[]).map(
                    (c) => (
                      <option key={c} value={c}>
                        {QUALIFICATION_CATEGORY_LABELS[c]}
                      </option>
                    ),
                  )}
                </select>
              </div>
            </label>
          ) : null}
        </div>

        <div className="sd__row">
          <Input
            label="Specialitate"
            list="sd-specs"
            value={form.spec}
            onChange={(e) => set('spec', e.target.value)}
          />
          {/* Sugestii, nu o listă închisă. Aplicația veche avea un `select` cu
              exact aceste nouă valori, iar o clinică cu un parodontolog nu
              avea unde să-l pună; câmpul liber, în schimb, adună „Ortodont",
              „ortodont" și „Medic Ortodont" ca specialități diferite în
              roster și în căutare. Datalist-ul le împacă. */}
          <datalist id="sd-specs">
            {SPECS.map((x) => (
              <option key={x} value={x} />
            ))}
          </datalist>
          <label className="field">
            <span className="field__label">{t('culoareInCalendar')}</span>
            <div className="field__control">
              <input
                type="color"
                value={form.color}
                onChange={(e) => set('color', e.target.value)}
              />
            </div>
          </label>
        </div>

        <div className="sd__row">
          <Input
            label="Email"
            type="email"
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
          />
          <Input
            label="Telefon"
            value={form.phone}
            onChange={(e) => set('phone', e.target.value)}
          />
        </div>

        {(branches.data?.length ?? 0) > 1 ? (
          <label className="field">
            <span className="field__label">{t('filiala')}</span>
            <div className="field__control">
              <select value={form.branchId} onChange={(e) => set('branchId', e.target.value)}>
                <option value="">{t('faraFiliala')}</option>
                {branches.data!.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
          </label>
        ) : null}

        <div className="sd__row">
          <Input
            label={t('incepe')}
            type="time"
            value={form.workStart || look.data?.workStart || '09:00'}
            onChange={(e) => set('workStart', e.target.value)}
          />
          <Input
            label={t('termina')}
            type="time"
            value={form.workEnd || look.data?.workEnd || '18:00'}
            onChange={(e) => set('workEnd', e.target.value)}
          />
        </div>

        <div className="sd__row">
          <Input
            label={t('pauzaDeLa')}
            type="time"
            value={form.lunchStart}
            onChange={(e) => set('lunchStart', e.target.value)}
          />
          <Input
            label={t('pauzaPanaLa')}
            type="time"
            value={form.lunchEnd}
            onChange={(e) => set('lunchEnd', e.target.value)}
          />
        </div>

        {canSeePersonal ? (
          <>
            <div className="sd__row">
              <Input
                label={t('functia')}
                maxLength={120}
                placeholder={t('exMedicStomatolog')}
                value={form.jobTitle}
                onChange={(e) => set('jobTitle', e.target.value)}
                hint={t('cumScriePeContract')}
                list="sd-job-positions"
              />
              <datalist id="sd-job-positions">
                {JOB_POSITIONS.map((p) => (
                  <option key={p} value={p} />
                ))}
              </datalist>
              <Input
                label="IDNP"
                inputMode="numeric"
                maxLength={20}
                value={form.idnp}
                onChange={(e) => set('idnp', e.target.value)}
                hint={t('k13CifreDinBuletin')}
              />
            </div>
            <div className="sd__row">
              <Input
                label={t('adresaDeDomiciliu')}
                maxLength={300}
                value={form.address}
                onChange={(e) => set('address', e.target.value)}
              />
              {canSeeSalary ? (
                <Input
                  label={t('salariulLunarDeBaza')}
                  inputMode="decimal"
                  value={form.baseSalary}
                  onChange={(e) => set('baseSalary', e.target.value)}
                  hint={t('dinElSeCalculeaza')}
                />
              ) : null}
            </div>
            <div className="sd__row">
              <Input
                label={t('zileDeConcediuPe')}
                type="number"
                min={0}
                max={365}
                step={1}
                inputMode="numeric"
                placeholder={hrRules.data ? String(hrRules.data.leaveDaysYear) : undefined}
                value={form.leaveDaysYear}
                onChange={(e) => set('leaveDaysYear', e.target.value)}
                hint={
                  hrRules.data
                    ? `Gol: norma clinicii, acum ${hrRules.data.leaveDaysYear} — și o urmează dacă se schimbă`
                    : t('golNormaCliniciiDin')
                }
              />
            </div>
          </>
        ) : null}

        <label className="sd__check">
          <input
            type="checkbox"
            checked={form.showInCalendar}
            onChange={(e) => set('showInCalendar', e.target.checked)}
          />
          {t('afiseazaInCalendar')}
        </label>
        <p className="sd__hint">{t('afiseazaInCalendarHint')}</p>

        {member ? (
          <label className="sd__check">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => set('active', e.target.checked)}
            />
            {t('activInEchipa')}
            <span className="sd__hint">{t('dezactivareaIiTaieAccesul')}</span>
          </label>
        ) : null}

        {error ? (
          <p className="sd__error" role="alert">
            {error}
          </p>
        ) : null}

        <footer className="sd__actions">
          <Button type="button" onClick={onClose}>
            {t('renunta')}
          </Button>
          <Button type="submit" variant="primary" loading={save.isPending}>
            {t('salveaza')}
          </Button>
        </footer>
      </form>
    </Modal>
  );
}
